import { toContentLocale } from "../../config/locales";
import EN_DE from "./locales/de.json";
import EN_ES from "./locales/es.json";
import EN_FR from "./locales/fr.json";
import EN_IT from "./locales/it.json";
import EN_NL from "./locales/nl.json";

const flipObject = obj => {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [v, k]));
};

const EN_TO_LOCALE = {
  de: EN_DE,
  es: EN_ES,
  fr: EN_FR,
  it: EN_IT,
  nl: EN_NL,
};

const LOCALE_TO_EN = {
  de: flipObject(EN_DE),
  es: flipObject(EN_ES),
  fr: flipObject(EN_FR),
  it: flipObject(EN_IT),
  nl: flipObject(EN_NL),
};

/*
 * Path words are per content language: `/en-us` uses the English words
 * (WHO-5766), so both ends go through `toContentLocale` first.
 */
const getTranslation = (value, routedSource, routedDestination) => {
  const source = toContentLocale(routedSource);
  const destination = toContentLocale(routedDestination);

  if (source === destination) {
    return value;
  }
  if (source === "en") {
    return EN_TO_LOCALE[destination][value] ?? value;
  }
  if (destination === "en") {
    return LOCALE_TO_EN[source][value] ?? value;
  }
  return (
    EN_TO_LOCALE[destination][LOCALE_TO_EN[source][value] ?? value] ?? value
  );
};

/**
 *  This function takes url (or href) and source and destination locale.
 *
 * @param {string} href - The url that needs to be localized
 * @param {import('next/router').Locale} sourceLocale - The current locale of the url
 * @param {import('next/router').Locale} destinationLocale - The locale in which the url should be returned
 * @returns {string} - The localized url
 */
const localizeHref = (href, sourceLocale, destinationLocale) => {
  if (href.startsWith("http")) {
    return href;
  }
  if (!destinationLocale) {
    return href;
  }
  if (sourceLocale === destinationLocale) {
    return href;
  }
  try {
    // A parse base, not the canonical host: only `url.pathname` is read below,
    // so the origin never reaches the output. Left as a literal for that reason.
    // eslint-disable-next-line no-restricted-syntax -- parse base, see above
    const url = new URL(href, "https://www.whoppah.com");
    const newPathname = url.pathname
      .split("/")
      .map(path => getTranslation(path, sourceLocale, destinationLocale))
      .join("/");
    return `${newPathname}${url.search}`;
  } catch (e) {
    return href;
  }
};

export default localizeHref;
